import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAuth0 } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { IS_LOCALHOST } from 'constants/Global.constants';
import { PUBLIC_PATHS } from 'constants/PublicPaths.constants';
import { ApiProvider } from 'contexts/ApiContext';
import { CookieConsentProvider } from 'contexts/CookieConsentContext';
import { OverrideIdProvider } from 'contexts/OverrideIdContext';
import { PreviousLocationProvider } from 'contexts/PreviousLocationContext';
import { ProjectLoadingProvider } from 'contexts/ProjectLoadingContext';
import { SingleAudioTrackProvider } from 'contexts/SingleAudioTrackContext';
import { saveTableState } from 'ducks/table/persist';
import { Provider } from 'react-redux';
import {
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';
import { OpentableWorkspaceScreen } from 'routes/legacyRoutes';
import {
  PAGE_TITLES,
  PATHS,
  PROJECT_ROUTE_PATTERN,
} from 'routes/routes.constants';
import { Toaster } from 'sonner';
import { ThemeProvider } from 'styled-components';

import { usePathname } from '@polyai/common/hooks/useRouter';
import { extractDomainFromEmail } from '@polyai/common/lib/emailHelper';
import debounce from '@polyai/common/utils/debounce';
import { Loader } from '@polyai/ui/components/atoms/Loader';
import { TOAST_DURATION } from '@polyai/ui/components/atoms/Toast';
import { getAppTheme } from '@polyai/ui/styles/theme';

import { store } from 'reduxStore';
import QueryClientProvider from 'api/queryClient';
import { LOGGER_TYPE } from 'api/types';
import { Auth } from 'auth';
import ProjectLoadingOverlay from 'components/atoms/ProjectLoadingOverlay';
import { Banner } from 'components/molecules/Banner';
import BuilderAssistant from 'components/molecules/BuilderAssistant';
import ErrorBoundary from 'components/molecules/ErrorBoundary';
import LazyScreen from 'components/molecules/LazyScreen';
import PrivatePage from 'components/molecules/PrivatePage';
import ApiConfiguration from 'components/organisms/DataAccessApiConfiguration';
import LandingLayout from 'components/organisms/LandingLayout';
import { useAppSelector } from 'hooks/reduxHooks';
import { isPolyEmail } from 'hooks/useProjectResources';
import useProjectRoute from 'hooks/useProjectRoute';
import { useResolvedTheme } from 'hooks/useResolvedTheme';
import { useTrackingConsent } from 'hooks/useTrackingConsent';
import { setupGlobalErrorHandlers } from 'lib/ddlogHelper';
import agentWizardRoutes from 'screens/AgentWizard';
import AsrAnnotationReviewScreen from 'screens/AsrAnnotationReview';
import AsrAnnotationsScreen from 'screens/AsrAnnotations';
import CallbackPage from 'screens/Callback';
import ConversationShortcut from 'screens/ConversationShortcut';
import ErrorPage, { ErrorStatus } from 'screens/ErrorPage';
import KnowledgeBaseShortcut from 'screens/KnowledgeBaseShortcut';
import LatencyVizScreen from 'screens/LatencyViz';
import Logout from 'screens/Logout';
import OpenTableErrorPage from 'screens/OpenTableErrorPage';
import OpenTableNewAgent from 'screens/OpenTableNewAgent';
import OpenTableRegion from 'screens/OpenTableRegion';
import OtLoginPage from 'screens/OTLogin/OTLogin';
import ParagonOAuthRedirect from 'screens/ParagonOAuthRedirect';
import TrialExpired from 'screens/TrialExpired/TrialExpired';
import GlobalStyle from '../styles/global';
import ProjectRedirect from './ProjectRedirect';
import { Auth0Wrapper } from 'Auth0Wrapper';

import 'styles/fonts.css';

import { isOTUserSet } from 'screens/OTLogin/utils';

const AssistantNavigation = lazy(
  () => import('components/organisms/AssistantNavigation'),
);
const CreateAssistantPage = lazy(() => import('screens/CreateAssistant'));
const FeatureSwitchScreen = lazy(() => import('screens/FeatureSwitch'));
const OTCallbackScreen = lazy(() => import('screens/OTCallback'));
const ProjectPage = lazy(() => import('./ProjectPage'));

if (process.env.NODE_ENV === 'development') {
  console.log(
    'PolyAI environment:',
    process.env.NEXT_PUBLIC_POLYAI_ENVIRONMENT,
  );
}

declare global {
  interface Window {
    Cypress: any;
    pendo: any;
    webkitAudioContext: typeof AudioContext;
  }
}

store.subscribe(
  debounce(() => {
    saveTableState(store.getState().table);
  }, 1000),
);

const App = () => {
  const authStatus = useAppSelector(state => state.auth.authStatus);
  const showUnauthorised = useAppSelector(state => state.auth.showUnauthorised);
  const { user, isLoading } = useAuth0();
  const { hasStatisticsConsent, isConsentReady } = useTrackingConsent();
  const rumInitializedRef = useRef(false);
  const pathname = usePathname();

  const isPublic = PUBLIC_PATHS.some(
    p => pathname === p || pathname.startsWith(p),
  );

  // Initialize DataDog Logs and loggers once on mount (not dependent on consent)
  // Logs are used for operational logging, not analytics
  useEffect(() => {
    if (IS_LOCALHOST || window.parent.Cypress) {
      return;
    }

    const region = process.env.NEXT_PUBLIC_POLYAI_REGION;
    const cluster = process.env.NEXT_PUBLIC_POLYAI_CLUSTER!;
    const ddSite =
      region?.toLowerCase() === 'us-1' ? 'datadoghq.com' : 'datadoghq.eu';

    datadogLogs.init({
      clientToken: process.env.NEXT_PUBLIC_DD_LOGS_CLIENT_TOKEN!,
      version: process.env.NEXT_PUBLIC_APP_VERSION!,
      site: ddSite,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: 'jupiter-ui',
    });

    for (const logger of Object.values(LOGGER_TYPE)) {
      datadogLogs.createLogger(logger, {
        level: 'info',
        handler: 'http',
        context: {
          env: cluster,
          cluster,
        },
      });
    }

    // Set up global error handlers for uncaught errors and unhandled rejections
    setupGlobalErrorHandlers();
  }, []);

  // Initialize DataDog RUM only when user has given statistics consent
  useEffect(() => {
    if (
      IS_LOCALHOST ||
      window.parent.Cypress ||
      !isConsentReady ||
      !hasStatisticsConsent ||
      rumInitializedRef.current
    ) {
      return;
    }

    const region = process.env.NEXT_PUBLIC_POLYAI_REGION;
    const ddSite =
      region?.toLowerCase() === 'us-1' ? 'datadoghq.com' : 'datadoghq.eu';

    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DD_APP_ID!,
      clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN!,
      env: process.env.NEXT_PUBLIC_POLYAI_CLUSTER!,
      version: process.env.NEXT_PUBLIC_APP_VERSION!,
      site: ddSite,
      service: 'jupiter-ui',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      actionNameAttribute: 'data-dd-action',
      defaultPrivacyLevel: 'allow',
      trackViewsManually: true,
      allowedTracingUrls: [
        process.env.NEXT_PUBLIC_POLYAI_API!,
        process.env.NEXT_PUBLIC_SOURCERER_API!,
        process.env.NEXT_PUBLIC_PINGU_API!,
      ],
    });

    rumInitializedRef.current = true;
  }, [isConsentReady, hasStatisticsConsent]);

  useEffect(() => {
    // Don't set user data if consent not ready or not granted
    if (!isConsentReady || !hasStatisticsConsent || IS_LOCALHOST) {
      return;
    }

    datadogRum.setUser({
      id: user?.email,
      name: user?.name,
      email: user?.email,
      email_domain: extractDomainFromEmail(user?.email),
    });
  }, [user, hasStatisticsConsent, isConsentReady]);

  useEffect(() => {
    // Don't set context properties if consent not ready or not granted
    if (!isConsentReady || !hasStatisticsConsent || IS_LOCALHOST) {
      return;
    }

    const userEmail = user?.email;
    if (userEmail === undefined) {
      datadogRum.removeGlobalContextProperty('user_email');
      datadogLogs.removeGlobalContextProperty('user_email');
      return;
    }
    const isInternal = isPolyEmail(userEmail);
    datadogRum.setGlobalContextProperty('user_email', userEmail);
    datadogRum.setGlobalContextProperty(
      'user_type',
      isInternal ? 'internal' : 'external',
    );
    datadogLogs.setGlobalContextProperty('user_email', userEmail);
    datadogLogs.setGlobalContextProperty(
      'user_type',
      isInternal ? 'internal' : 'external',
    );
  }, [user?.email, hasStatisticsConsent, isConsentReady]);

  if (showUnauthorised) {
    return <ErrorPage type={ErrorStatus.UNAUTHORIZED} />;
  }

  // Check if we're on a project route (matches /:accountId/PROJECT-xxx/...)
  const isProjectRoute = PROJECT_ROUTE_PATTERN.test(pathname);

  if (!isPublic && (isLoading || authStatus !== 'authorized')) {
    // Show error page if user is not authorised in Aegis and is an OT user
    if (authStatus === 'unauthorized' && isOTUserSet()) {
      return <OpenTableErrorPage />;
    }

    // For project routes, don't show Loader
    if (!isProjectRoute) {
      return <Loader inProjectPage={false} fullScreen logo />;
    }
  }

  return (
    <SingleAudioTrackProvider>
      <PreviousLocationProvider>
        <Suspense fallback={<Loader inProjectPage={false} fullScreen logo />}>
          <Routes>
            <Route
              element={<OpenTableRegion />}
              path={PATHS.OPENTABLE_REGION}
            />
            <Route element={<OtLoginPage />} path={PATHS.OPENTABLE_LOGIN} />
            <Route
              element={<OTCallbackScreen />}
              path={PATHS.OPENTABLE_CALLBACK}
            />
            <Route
              element={<ParagonOAuthRedirect />}
              path={PATHS.PARAGON_OAUTH_REDIRECT}
            />
            <Route element={<Logout />} path={PATHS.LOGOUT} />
            <Route element={<ProjectPage />} path="/templates/:projectId/*" />
            <Route element={<ProjectRedirect />} path="/templates/:projectId" />
            {authStatus === 'authorized' && (
              <Route
                element={
                  <Banner>
                    <Outlet />
                  </Banner>
                }
              >
                <Route element={<CallbackPage />} path={PATHS.CALLBACK} />
                <Route element={<TrialExpired />} path={PATHS.TRIAL_EXPIRED} />
                <Route
                  element={
                    <PrivatePage ErrorPage={ErrorPage}>
                      <OpentableWorkspaceScreen />
                    </PrivatePage>
                  }
                  path={PATHS.OPENTABLE_WORKSPACE}
                />
                <Route
                  element={
                    <PrivatePage ErrorPage={ErrorPage}>
                      <OpenTableNewAgent />
                    </PrivatePage>
                  }
                  path={PATHS.OPENTABLE_NEW_AGENT}
                />
                <Route
                  element={
                    <LazyScreen>
                      <AsrAnnotationsScreen />
                    </LazyScreen>
                  }
                  path="/asr-annotations"
                />
                <Route
                  element={
                    <LazyScreen>
                      <AsrAnnotationReviewScreen />
                    </LazyScreen>
                  }
                  path="/asr-annotations/:taskId"
                />
                <Route
                  element={
                    <LazyScreen noWrap>
                      <LatencyVizScreen />
                    </LazyScreen>
                  }
                  path={PATHS.LATENCY_VIZ}
                />
                <Route
                  element={
                    <PrivatePage ErrorPage={ErrorPage}>
                      <LandingLayout />
                    </PrivatePage>
                  }
                  path={PATHS.ASSISTANTS}
                >
                  <Route
                    element={
                      <LazyScreen title={PAGE_TITLES.ASSISTANTS}>
                        <AssistantNavigation tab="agents" />
                      </LazyScreen>
                    }
                    path=""
                  />
                  <Route
                    element={
                      <LazyScreen title={PAGE_TITLES.CREATE_ASSISTANT}>
                        <CreateAssistantPage />
                      </LazyScreen>
                    }
                    path={PATHS.CREATE_ASSISTANT.replace(
                      `${PATHS.ASSISTANTS}/`,
                      '',
                    )}
                  />
                  <Route
                    element={
                      <LazyScreen title={PAGE_TITLES.BILLING}>
                        <AssistantNavigation tab="billing" />
                      </LazyScreen>
                    }
                    path={PATHS.BILLING.replace(`${PATHS.ASSISTANTS}/`, '')}
                  />
                  <Route
                    element={
                      <LazyScreen title={PAGE_TITLES.SECRETS}>
                        <AssistantNavigation tab="secrets" />
                      </LazyScreen>
                    }
                    path={PATHS.SECRETS.replace(`${PATHS.ASSISTANTS}/`, '')}
                  />
                  <Route
                    element={
                      <LazyScreen title={PAGE_TITLES.DATA_ACCESS}>
                        <AssistantNavigation tab="data-access" />
                      </LazyScreen>
                    }
                    path={PATHS.DATA_ACCESS.replace(`${PATHS.ASSISTANTS}/`, '')}
                  />
                  <Route
                    element={
                      <LazyScreen title={PAGE_TITLES.API_KEYS}>
                        <AssistantNavigation tab="api-keys" />
                      </LazyScreen>
                    }
                    path={PATHS.API_KEYS.replace(`${PATHS.ASSISTANTS}/`, '')}
                  />
                  <Route
                    element={
                      <LazyScreen
                        title={PAGE_TITLES.DATA_ACCESS_API_CONFIGURATION}
                      >
                        <ApiConfiguration />
                      </LazyScreen>
                    }
                    path={PATHS.DATA_ACCESS_API_CONFIGURATION.replace(
                      `${PATHS.ASSISTANTS}/`,
                      '',
                    )}
                  />
                  <Route
                    element={
                      <LazyScreen title={PAGE_TITLES.USERS}>
                        <AssistantNavigation tab="users" />
                      </LazyScreen>
                    }
                    path={PATHS.USERS.replace(`${PATHS.ASSISTANTS}/`, '')}
                  />
                </Route>
                <Route
                  element={
                    <PrivatePage ErrorPage={ErrorPage}>
                      <FeatureSwitchScreen />
                    </PrivatePage>
                  }
                  path={PATHS.ADMIN_PAGE}
                />
                {agentWizardRoutes()}
                <Route
                  element={
                    <PrivatePage ErrorPage={ErrorPage}>
                      <ProjectPage />
                    </PrivatePage>
                  }
                  path="/:accountId/:projectId/*"
                />
                <Route
                  element={<ProjectRedirect />}
                  path="/:accountId/:projectId"
                />
                <Route
                  element={<ConversationShortcut />}
                  path="/conversation"
                />
                <Route element={<KnowledgeBaseShortcut />} path="/knowledge" />
                <Route
                  element={<ErrorPage type={ErrorStatus.NOT_FOUND} />}
                  path="*"
                />
              </Route>
            )}
          </Routes>
        </Suspense>

        {authStatus === 'authorized' && !pathname.includes('/agent-wizard') && (
          <BuilderAssistant />
        )}
      </PreviousLocationProvider>
    </SingleAudioTrackProvider>
  );
};

const AppContent = () => {
  return (
    <ProjectLoadingProvider>
      <ProjectLoadingOverlay />
      <App />
    </ProjectLoadingProvider>
  );
};

const TOASTER_ROOT_ID = 'toaster-root';

export function ToasterPortal() {
  const [container, setContainer] = useState<Element | null>(null);

  useEffect(() => {
    const element = document.getElementById(TOASTER_ROOT_ID) ?? document.body;
    setContainer(element);
  }, []);

  if (!container) {
    return null;
  }

  return createPortal(
    <Toaster toastOptions={{ unstyled: true, duration: TOAST_DURATION }} />,
    container,
  );
}

const AppWithinStore = () => {
  const resolvedTheme = useResolvedTheme();
  const { accountId, projectId } = useProjectRoute();

  return (
    <ThemeProvider theme={getAppTheme(resolvedTheme)}>
      <ApiProvider>
        <GlobalStyle />
        <CookieConsentProvider>
          <Auth0Wrapper>
            <Auth>
              <ToasterPortal />
              <QueryClientProvider>
                <ErrorBoundary accountId={accountId} projectId={projectId}>
                  <AppContent />
                </ErrorBoundary>
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </Auth>
          </Auth0Wrapper>
        </CookieConsentProvider>
      </ApiProvider>
    </ThemeProvider>
  );
};

const AppWrapper = () => {
  return (
    <Router>
      <Provider store={store}>
        <OverrideIdProvider>
          <AppWithinStore />
        </OverrideIdProvider>
      </Provider>
    </Router>
  );
};

export default AppWrapper;
